import {getDateFormat, isCurp, sanitizeString} from "../../../Apis/functions";
import {getUrlParameters} from "../../Dashboard/Visitor/External/Model/Model";
import routes from "../../../Routes/routes";
import {MODEL_LANGUAGE} from "../../../components/Language/Model/Models";

export const DOCUMENT_ID_MODEL = {
    CURP: {
        name: "CURP",
        maxChanges: 8
    },
    PASSPORT:  {
        name: "PASSPORT",
        maxChanges: 4
    }
}
export const DEVICE_ROTATED_ON_ORIENTATION = {
    PORTRAIT: 'portrait',
    NONE: 'none',
    LANDSCAPE: 'landscape'
}

export const LIVENESS_MODEL = {
    ACTIVE: 'active',
    PASSIVE: 'passive'
}

export const MODEL_VERIDAS = {
    name: '',
    lastName: '',
    secondLastName: '',
    dob: null,
    gender: null,
    documentId: '',
    documentIdOriginal:'',
    maxChanges:0, //For allow or denied documentId changes for limit
    maxDeletes:0,
    isValid:function (){
        return sanitizeString(this.name) !== null && sanitizeString(this.lastName) !== null
            && this.dob !== null && this.gender !== null && sanitizeString(this.documentId) !== null;
    },
    toRegister: function () {
        return {
            name: sanitizeString(this.name),
            lastName: sanitizeString(this.lastName),
            secondLastName: sanitizeString(this.secondLastName),
            dob: this.dob,
            gender: this.gender,
            documentId: sanitizeString(this.documentId)
        }
    },
    set:function (data){
        const maxChanges = isCurp(data?.documentId) ? DOCUMENT_ID_MODEL.CURP.maxChanges : DOCUMENT_ID_MODEL.PASSPORT.maxChanges;
        return {
            ...this,
            name: data?.name || '',
            lastName: data?.lastName || '',
            secondLastName: data?.secondLastName || '',
            dob: data?.dob ? getDateFormat(data.dob, false) : null,
            gender: data?.gender || null,
            documentId: data?.documentId || '',
            documentIdOriginal: data?.documentId || '',
            maxChanges: maxChanges,
            maxDeletes: maxChanges
        }
    },
}

export const getSdkLanguage = (langCode) => langCode === MODEL_LANGUAGE.SP.code ? MODEL_LANGUAGE.SP.transMd : MODEL_LANGUAGE.EN.transMd;

export const loadVdSdk = (tagName, src, scriptOptions = {}) => {
    return new Promise((resolve, reject) => {
        if (customElements.get(tagName)) {
            resolve();
            return;
        }

        const existingScript = document.querySelector(`script[src="${src}"]`);

        if (existingScript) {
            customElements.whenDefined(tagName).then(resolve).catch(reject);
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        Object.assign(script, scriptOptions);

        script.onload = () => {
            customElements.whenDefined(tagName).then(resolve).catch(reject);
        };

        script.onerror = reject;
        document.body.appendChild(script);
    });
};

export const loadScript = (src, callback = null, onError = null) => {
    const isScript = document.querySelector(`script[src="${src}"]`);
    if (isScript) {
        if (callback) callback();
        return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.type = 'application/javascript';
    script.async = true;

    script.onload = () => {
        if (callback) callback();
    };

    script.onerror = () => {
        if (onError) onError();
    };

    document.head.appendChild(script);
};

/**
 * Key of the apartment the building registration link carried. The flow keeps it
 * out of redux as well: reloading any step of the identity validation would lose
 * it and the resident would be sent back to the identify you form that the link
 * of one resident never had.
 */
const BUILDING_APARTMENT_STORAGE_KEY = 'buildingApartmentId';

/**
 * Remembers the apartment of the registration link so every step of the flow
 * resolves the same way after a reload.
 *
 * @param {string} buildingApartmentId - Apartment the link was copied for.
 */
export const setStoredBuildingApartmentId = (buildingApartmentId) => {
    if (buildingApartmentId) localStorage.setItem(BUILDING_APARTMENT_STORAGE_KEY, buildingApartmentId);
};

/**
 * Apartment of the registration link the resident opened, if there was one.
 *
 * @returns {string|null} Stored apartment.
 */
export const getStoredBuildingApartmentId = () => localStorage.getItem(BUILDING_APARTMENT_STORAGE_KEY);

/**
 * Forgets the apartment of the registration link, the registration it belonged
 * to is over or a different one started.
 */
export const removeStoredBuildingApartmentId = () => {
    localStorage.removeItem(BUILDING_APARTMENT_STORAGE_KEY);
};

/**
 * Returns the main navigation URL parameters based on the available data.
 *
 * If a `buildingId` is provided, it returns the building registration route of that building.
 * If an `eventId` is provided, it returns the event main route.
 * Otherwise, it returns the invitation details route.
 *
 * @param {string} invitationId - Invitation identifier.
 * @param {string} [userId] - User the registration belongs to, it is optional.
 * @param {string} [eventId] - Event identifier.
 * @param {string} [buildingId] - Building the registration belongs to.
 * @param {string} [buildingApartmentId] - Apartment the registration link carried, it
 *   travels in the place of the building on that flow and it is the one that tells
 *   the registration started from the link of one resident. It falls back to the
 *   stored one so a reload keeps resolving the same way.
 * @param {string} [guardFlowId] - Guard flow identifier.
 * @returns {string} Generated main URL.
 */
export const getMainUrl = ({ invitationId, userId, eventId, buildingId, buildingApartmentId, guardFlowId }) => {
    const resolvedApartmentId = buildingApartmentId ?? getStoredBuildingApartmentId();

    // The link copied for one resident already carries their user, going back
    // returns to it and never to the identify you form
    if (resolvedApartmentId && resolvedApartmentId === buildingId)
        return getUrlParameters({
            url: `${routes.buildingRegistration.main}${resolvedApartmentId}/${userId}`
        });

    if (buildingId)
        return getUrlParameters({
            url: `${routes.buildingRegistration.identifyYou}${buildingId}${userId ? `/${userId}` : ''}`
        });

    return eventId
        ? getUrlParameters({
            url: `${routes.eventDetails.main}${eventId}`
        })
        : getUrlParameters({
            url: routes.invitationDetails.invitation,
            id: invitationId,
            guardFlowId
        });
}

/**
 * Returns the validation failure URL parameters based on the available data.
 *
 * If a `buildingId` is provided, the building registration failure route is used.
 * If an `eventId` is provided, the event validation failure route is used.
 * Otherwise, the Veridas integration validation failure route is returned.
 *
 * @param {string} invitationId - Invitation identifier.
 * @param userId
 * @param {string} [eventId] - Event identifier.
 * @param {string} [buildingId] - Building the registration belongs to.
 * @param {string} [guardFlowId] - Guard flow identifier.
 * @returns {string} Generated validation failure URL.
 */
export const getFailedUrl = ({ invitationId, userId, eventId, buildingId, guardFlowId }) => {
    if (buildingId)
        return getUrlParameters({
            url: `${routes.buildingRegistration.validationFail}${userId}/${buildingId}`
        });

    return eventId
        ? getUrlParameters({
            url: `${routes.eventDetails.validationFail}${userId}/${eventId}`
        })
        : getUrlParameters({
            url: routes.veridasIntegration.validationFail,
            id: invitationId,
            guardFlowId
        });
}

/**
 * Returns the validation success URL parameters based on the available data.
 *
 * If a `buildingId` is provided, the building registration success route is used.
 * If an `eventId` is provided, the event validation success route is used.
 * Otherwise, the Veridas integration validation success route is returned.
 *
 * @param {string} invitationId - Invitation identifier.
 * @param userId
 * @param {string} [eventId] - Event identifier.
 * @param {string} [buildingId] - Building the registration belongs to.
 * @param {string} [guardFlowId] - Guard flow identifier.
 * @returns {string} Generated validation success URL.
 */
export const getSuccessUrl = ({ invitationId, userId, eventId, buildingId, guardFlowId }) => {
    if (buildingId)
        return getUrlParameters({
            url: `${routes.buildingRegistration.successValidation}${userId}/${buildingId}`
        });

    return eventId
        ? getUrlParameters({
            url: `${routes.eventDetails.successValidation}${userId}/${eventId}`
        })
        : getUrlParameters({
            url: routes.veridasIntegration.successValidation,
            id: invitationId,
            guardFlowId
        });
}
